import { useState } from "react";

import useData from "./useData";
import usePage from "./usePage";

const useAdpConfiguration = () => {
  const { getFromServer, executeAction, updateRecord, deleteRecord } =
    useData();
  const { showMessage } = usePage();

  const [loading, setLoading] = useState(false);
  const [adpCodes, setAdpCodes] = useState([]);
  const [clients, setClients] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [adjustmentTypes, setAdjustmentTypes] = useState([]);

  const loadAdpCodes = async () => {
    const results = await getFromServer({ entityName: "AdpCode" });
    setAdpCodes(Array.isArray(results) ? results : []);
    return Array.isArray(results) ? results : [];
  };

  const loadClients = async () => {
    const results = await getFromServer({ entityName: "Client" });
    setClients(Array.isArray(results) ? results : []);
    return Array.isArray(results) ? results : [];
  };

  const loadMappings = async () => {
    const results = await executeAction({
      entityName: "AdpClientMapping",
      action: "listWithDetails",
      data: {},
    });

    const data =
      results?.isSuccessful && Array.isArray(results.data) ? results.data : [];
    setMappings(data);
    return data;
  };

  const loadAdjustmentTypes = async () => {
    const results = await getFromServer({ entityName: "AdjustmentType" });
    const data = Array.isArray(results) ? results : [];
    data.sort((a, b) => {
      const sortA = Number.isFinite(a?.sortOrder) ? a.sortOrder : 0;
      const sortB = Number.isFinite(b?.sortOrder) ? b.sortOrder : 0;
      if (sortA !== sortB) return sortA - sortB;
      return String(a?.label || a?.key || "").localeCompare(
        String(b?.label || b?.key || ""),
      );
    });
    setAdjustmentTypes(data);
    return data;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAdpCodes(),
        loadClients(),
        loadMappings(),
        loadAdjustmentTypes(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const saveAdpCode = async (data) => {
    const parsedValue =
      data?.value === "" || data?.value === null || data?.value === undefined
        ? null
        : Number(data.value);

    const payload = {
      ...data,
      code: data?.code ? String(data.code).toUpperCase().trim() : "",
      codeType: data?.codeType
        ? String(data.codeType).toUpperCase().trim()
        : "",
      value: Number.isFinite(parsedValue) ? parsedValue : null,
    };

    const results = await updateRecord("AdpCode", payload, false);
    if (results?.isSuccessful) {
      showMessage("saved", "success");
      await loadAdpCodes();
      return true;
    }

    showMessage(results?.message || "Failed to save ADP code", "error");
    return false;
  };

  const removeAdpCode = async (id) => {
    try {
      await deleteRecord("AdpCode", id);
      await loadAdpCodes();
      return true;
    } catch (err) {
      showMessage(err.message, "error");
      return false;
    }
  };

  const saveMapping = async (data) => {
    const payload = {
      ...data,
      transactionType: data?.transactionType
        ? String(data.transactionType).toLowerCase().trim()
        : "",
      expenseType:
        data?.transactionType === "expense" ? data.expenseType || null : null,
      adjustmentType:
        data?.transactionType === "adjustment"
          ? data.adjustmentType || null
          : null,
    };

    const results = await updateRecord("AdpClientMapping", payload, false);
    if (results?.isSuccessful) {
      showMessage("saved", "success");
      await loadMappings();
      return true;
    }

    showMessage(results?.message || "Failed to save mapping", "error");
    return false;
  };

  const removeMapping = async (id) => {
    try {
      await deleteRecord("AdpClientMapping", id);
      await loadMappings();
      return true;
    } catch (err) {
      showMessage(err.message, "error");
      return false;
    }
  };

  return {
    loading,
    adpCodes,
    clients,
    mappings,
    adjustmentTypes,
    loadAll,
    saveAdpCode,
    removeAdpCode,
    saveMapping,
    removeMapping,
  };
};

export default useAdpConfiguration;
